import { gql } from '@apollo/client';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { InfiniteScrollBoundary } from 'components/common/InfiniteScrollBoundary';
import { IconLinearSort } from 'components/icons/components/linear/IconLinearSort';
import {
  NestedFiltersMenuView,
  NestedFiltersOptionType,
} from 'features/nestedFilters';
import {
  SentimentColorMap,
  SentimentLabelMap,
} from 'features/socialListeningCommunity/constants';
import { SocialPostCardView } from 'features/socialPost';
import {
  CreatorProfilePaginatedBrandMentionsInputSortField,
  CreatorProfilesBrandMentionStatsInput,
  Sentiment,
  useGetBrandMentionSocialPostsForSlBrandMentionPostsQuery,
  useGetBrandMentionStatsForSlBrandMentionPostsQuery,
} from 'graphql/generated';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';

// eslint-disable-next-line
gql`
  query GetBrandMentionStatsForSLBrandMentionPosts(
    $input: CreatorProfilesBrandMentionStatsInput!
  ) {
    creatorProfilesBrandMentionStats(input: $input) {
      percentageChange
      sentiment
      totalEngagement
      totalPosts
      totalViews
    }
  }
`;

// eslint-disable-next-line
gql`
  query GetBrandMentionSocialPostsForSlBrandMentionPosts(
    $input: CreatorProfilesBrandMentionSocialPostsInput!
  ) {
    creatorProfilesBrandMentionSocialPosts(input: $input) {
      data {
        id
        ...SocialPostFragmentSocialPostCardView
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const sortOptions = [
  {
    label: 'Play count',
    value: CreatorProfilePaginatedBrandMentionsInputSortField.PlayCount,
  },
  {
    label: 'Total like count',
    value: CreatorProfilePaginatedBrandMentionsInputSortField.DiggCount,
  },
  {
    label: 'Engagement rate',
    value: CreatorProfilePaginatedBrandMentionsInputSortField.EngagementRate,
  },
  {
    label: 'Recency',
    value: CreatorProfilePaginatedBrandMentionsInputSortField.Recency,
  },
];

interface SocialListeningBrandMentionPostsProps
  extends CreatorProfilesBrandMentionStatsInput {
  onClickPost: (postId: string) => void;
}

export const SocialListeningBrandMentionPosts = ({
  brandId,
  dateRange,
  onClickPost,
}: SocialListeningBrandMentionPostsProps) => {
  const [sortKey, setSortKey] =
    useState<CreatorProfilePaginatedBrandMentionsInputSortField>(
      CreatorProfilePaginatedBrandMentionsInputSortField.PlayCount,
    );

  const socialPostFilters = useMemo(() => {
    return {
      brandId,
      dateRange,
      sortBy: { field: sortKey },
    };
  }, [brandId, dateRange, sortKey]);

  const { data: mentionStatsData } =
    useGetBrandMentionStatsForSlBrandMentionPostsQuery({
      variables: {
        input: {
          brandId,
          dateRange,
        },
      },
      fetchPolicy: 'cache-and-network',
    });

  const {
    data: socialPostsData,
    loading: socialPostsLoading,
    fetchMore: fetchMoreSocialPosts,
  } = useGetBrandMentionSocialPostsForSlBrandMentionPostsQuery({
    variables: {
      input: socialPostFilters,
    },
    fetchPolicy: 'cache-and-network',
  });

  const socialPosts =
    socialPostsData?.creatorProfilesBrandMentionSocialPosts.data ?? [];

  const isNegativePercentChange = useMemo(() => {
    return mentionStatsData?.creatorProfilesBrandMentionStats?.percentageChange
      ? mentionStatsData?.creatorProfilesBrandMentionStats?.percentageChange < 0
      : false;
  }, [mentionStatsData]);

  const summaryData = useMemo(() => {
    const mentionStats = mentionStatsData?.creatorProfilesBrandMentionStats;
    return [
      {
        label: 'Total post count',
        value: formatBigNumber(mentionStats?.totalPosts ?? 0),
      },
      {
        label: 'Total view count',
        value: formatBigNumber(mentionStats?.totalViews ?? 0),
      },
      {
        label: 'Total engagement',
        value: formatBigNumber(mentionStats?.totalEngagement ?? 0),
      },
      {
        label: 'Percentage change',
        value: `${
          mentionStats?.percentageChange === 0
            ? ''
            : isNegativePercentChange
            ? '↓ '
            : '↑ '
        }${
          mentionStats?.percentageChange
            ? mentionStats?.percentageChange.toFixed(2).replace('-', '')
            : 0
        }%`,
        sx: {
          whiteSpace: 'nowrap',
          '.summary-value': {
            color:
              theme.colors?.utility[
                mentionStats?.percentageChange === 0
                  ? '900'
                  : isNegativePercentChange
                  ? 'pink-3'
                  : 'green-3'
              ],
          },
        },
      },
      {
        label: 'Overall Sentiment',
        value: SentimentLabelMap[mentionStats?.sentiment ?? Sentiment.Positive],
        sx: {
          whiteSpace: 'nowrap',
          '.summary-value': {
            color:
              SentimentColorMap[mentionStats?.sentiment ?? Sentiment.Positive],
          },
        },
      },
    ];
  }, [
    isNegativePercentChange,
    mentionStatsData?.creatorProfilesBrandMentionStats,
  ]);

  const renderSummary = useCallback((summary, sx) => {
    return (
      <Box
        flexDirection="column"
        display="flex"
        alignItems="flex-start"
        gap={3}
        pl={4}
        sx={sx}
      >
        <Typography variant="headline-sm">{summary.label}</Typography>
        <Typography
          fontSize={theme.spacing(8)}
          variant="headline-xl"
          color={theme.colors?.utility[900]}
          fontWeight={500}
          letterSpacing="-1.28px"
          className="summary-value"
        >
          {summary.value}
        </Typography>
      </Box>
    );
  }, []);
  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          backgroundColor: theme.colors?.primary.parchment,
          borderRadius: 2,
          py: 4,
        }}
      >
        {summaryData.map((summary, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={2.4}
            key={index}
            sx={{
              borderRight: {
                md:
                  index !== summaryData.length - 1
                    ? `1px solid rgba(35, 6, 3, 0.07)`
                    : 'none',
              },
            }}
          >
            {renderSummary(summary, { ...summary?.sx })}
          </Grid>
        ))}
      </Grid>

      <Divider
        sx={{
          bgcolor: 'rgba(35, 6, 3, 0.05)',
          height: 2,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="headline-lg"
            fontSize={20}
            color={theme.colors?.utility[900]}
          >
            Posts
          </Typography>
          <NestedFiltersMenuView
            values={{
              sort: {
                value: sortOptions.find((option) => option.value === sortKey),
              },
            }}
            items={[
              {
                type: 'single-select',
                key: 'sort',
                options: sortOptions,
              },
            ]}
            onChange={(values) => {
              setSortKey(
                (values.sort.value as NestedFiltersOptionType)
                  ?.value as CreatorProfilePaginatedBrandMentionsInputSortField,
              );
            }}
            componentsProps={{
              trigger: {
                text:
                  sortOptions.find((option) => option.value === sortKey)
                    ?.label ?? 'Sort',
                Icon: IconLinearSort,
                sx: {
                  bgcolor: theme.colors?.primary.parchment,
                },
              },
            }}
          />
        </Box>
        {socialPosts.length ? (
          <>
            <Grid container spacing={6}>
              {socialPosts.map((socialPost) => {
                return (
                  <Grid item xs={12} sm={6} md={3} lg={2.4} key={socialPost.id}>
                    <Box
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClickPost(socialPost.id);
                      }}
                    >
                      <SocialPostCardView socialPost={socialPost} />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <InfiniteScrollBoundary
              disabled={
                socialPostsLoading ||
                !socialPostsData?.creatorProfilesBrandMentionSocialPosts
                  .pageInfo.hasNextPage
              }
              onVisible={() => {
                fetchMoreSocialPosts({
                  variables: {
                    input: {
                      ...socialPostFilters,
                      skip: socialPostsData
                        ?.creatorProfilesBrandMentionSocialPosts.data.length,
                    },
                  },
                  updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    return {
                      creatorProfilesBrandMentionSocialPosts: {
                        ...fetchMoreResult.creatorProfilesBrandMentionSocialPosts,
                        data: [
                          ...prev.creatorProfilesBrandMentionSocialPosts.data,
                          ...fetchMoreResult
                            .creatorProfilesBrandMentionSocialPosts.data,
                        ],
                      },
                    };
                  },
                });
              }}
            />
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography
              variant="headline-sm"
              color={theme.colors?.utility[600]}
            >
              {socialPostsLoading ? 'Loading...' : 'No posts found'}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};
